import { actualCarrierCost } from './orderFinancials'
import PersonalizationDetails from './PersonalizationDetails'
import FinancialBreakdown from './FinancialBreakdown'
import React, { useState, useEffect } from 'react'
import ApiService from '../../../data/services/ApiService'
import toastr from 'toastr'
import 'toastr/build/toastr.min.css'
import {
    Card,
    Table,
    Row,
    Col,
    Input,
    Button,
    Label
} from 'reactstrap'
import CurrencyFormat from 'react-currency-format'
import { connect } from "react-redux"
import { withRouter } from 'react-router-dom'

const View = ({
    profile,
    salesOrderId,
    history
}) => {
    const [order, setOrder] = useState()
    const [actualCost, setActualCost] = useState('')
    const [costBusy, setCostBusy] = useState(false)
    const [costError, setCostError] = useState('')
    useEffect(() => {
        setActualCost(order?.shipmentMetadata?.actualShippingCost?.amount ?? '')
    }, [order])

    const saveCost = async (reconcile = false) => {
        if (costBusy) return
        if (!reconcile && actualCost !== '' && !/^\d+(\.\d{1,2})?$/.test(actualCost)) {
            setCostError('Ingresa un importe no negativo con hasta dos decimales.')
            return
        }
        setCostBusy(true)
        setCostError('')
        try {
            const response = reconcile ? await ApiService.reconcileShippingCost(order.id)
                : await ApiService.saveActualShippingCost(order.id, actualCost === '' ? null : actualCost)
            if (!response.data.success) throw new Error(response.data.message)
            setOrder(response.data.sale)
            toastr.success('Costo de envío guardado', '¡Éxito!')
        } catch (error) {
            setCostError(error?.response?.data?.message || error.message || 'No se pudo guardar el costo de envío.')
        } finally {
            setCostBusy(false)
        }
    }
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [confirming, setConfirming] = useState(false)
    const [confirmationError, setConfirmationError] = useState('')
    const [shippingMode, setShippingMode] = useState('skydropx')
    const [lineItems, setLineItems] = useState([])
    const [customer, setCustomer] = useState()
    const [trackingForm, setTrackingForm] = useState({
        carrier: '',
        trackingNumber: '',
        trackingUrl: '',
        shipmentProvider: '',
        shipmentProviderShipmentId: '',
        shipmentLabelUrl: ''
    })

    useEffect(() => {
        (async () => {
            const response = await ApiService.getSalesOrderById(salesOrderId)

            if (!response.data.success) {
                toastr.error(`No pude encontrar este pedido`, `Error!`)

                return
            }

            const { sale } = response.data

            setOrder(sale)
            setLineItems(sale.lineItems)
            setCustomer(sale.user)
            setTrackingForm({
                carrier: sale.carrier || '',
                trackingNumber: sale.trackingNumber || '',
                trackingUrl: sale.trackingUrl || '',
                shipmentProvider: sale.shipmentProvider || '',
                shipmentProviderShipmentId: sale.shipmentProviderShipmentId || '',
                shipmentLabelUrl: sale.shipmentLabelUrl || ''
            })
        })()
        // eslint-disable-next-line
    }, [salesOrderId])

    const _cancelOrder = async (id) => {
        try {
            const result = await ApiService.cancelSalesOrder(id)

            if (!result.data.success) {
                toastr.error(`No pude eliminar este pedido`, 'Error')

                return false
            }

            toastr.success(`Pedido Cancelado`, '¡Éxito!')
            window.analytics.track('Sales Order Canceled', {
                orderId: id
            })

            history.push(`/Sales-order`)
        } catch (error) {
            console.log(error)

            toastr.error(`No pude eliminar este pedido`, 'Error')
        }
    }

    const _confirmOrder = async (id) => {
        if (confirming) return
        setConfirming(true)
        setConfirmationError('')
        try {
            const result = await ApiService.confirmSalesOrder(id, { shippingMode, ...(shippingMode === 'manual' ? trackingForm : {}) })

            if (!result.data.success) {
                toastr.error(result.data.message || `No pude confirmar este pedido`, 'Error')

                return false
            }

            const shipmentId = result.data.shipment?.shipmentId
            toastr.success(
                shipmentId ? `Pedido confirmado. Envío SkyDropX ${shipmentId} creado.` : `Pedido confirmado`,
                '¡Éxito!'
            )
            window.analytics.track('Sales Order Confirmed', {
                orderId: id
            })

            setOrder(result.data.sale)
            setTrackingForm(Object.fromEntries(Object.keys(trackingForm).map(key => [key, result.data.sale[key] || ''])))
            setConfirmOpen(false)
        } catch (error) {
            const response = error?.response?.data
            const detail = response?.reason ? `${response.message || 'No pude confirmar este pedido'} (${response.reason})` : response?.message
            setConfirmationError(detail || 'No pude confirmar este pedido')
            toastr.error(detail || `No pude confirmar este pedido`, 'Error')
        } finally {
            setConfirming(false)
        }
    }

    const _saveTracking = async (id) => {
        try {
            const result = await ApiService.updateSalesOrderTracking({
                saleId: id,
                ...trackingForm
            })

            if (!result.data.success) {
                toastr.error(`No pude guardar los datos de rastreo`, 'Error')

                return false
            }

            toastr.success(`Datos de rastreo guardados`, '¡Éxito!')
            setOrder(result.data.sale)
        } catch (error) {
            toastr.error(`No pude guardar los datos de rastreo`, 'Error')
        }
    }

    const _markAsDelivered = async (id) => {
        try {
            const result = await ApiService.markSalesOrderAsDelivered(id)

            if (!result.data.success) {
                toastr.error(`No pude marcar este pedido como entregado`, 'Error')

                return false
            }

            toastr.success(`Pedido marcado como entregado`, '¡Éxito!')
            window.analytics.track('Sales Order Delivered', {
                orderId: id
            })

            setOrder(result.data.sale)
        } catch (error) {
            toastr.error(`No pude marcar este pedido como entregado`, 'Error')
        }
    }

    const _markAsShipped = async (id) => {
        try {
            const result = await ApiService.markSalesOrderAsShipped({
                saleId: id,
                ...trackingForm
            })

            if (!result.data.success) {
                toastr.error(`No pude marcar este pedido como enviado`, 'Error')

                return false
            }

            toastr.success(`Pedido marcado como enviado`, '¡Éxito!')
            window.analytics.track('Sales Order Shipped', {
                orderId: id
            })

            setOrder(result.data.sale)
        } catch (error) {
            toastr.error(`No pude marcar este pedido como entregado`, 'Error')
        }
    }

    const _updateTrackingField = (field, value) => {
        setTrackingForm({
            ...trackingForm,
            [field]: value
        })
    }

    const shippingQuote = order?.shippingQuote || {}
    const canManageCost = ['ADMIN', 'BACKOFFICE'].includes(profile?.role?.codeName)
    const manualShipment = order?.shipmentProvider !== 'skydropx' && !order?.shipmentMetadata?.skydropxShipment
        && (order?.shipmentProvider === 'manual' || order?.shipmentMetadata?.confirmationShippingMode === 'manual')
    const canConfirm = order?.status === 'paid'
    const canEditTracking = ['confirmed', 'shipped'].includes(order?.status) || (canConfirm && confirmOpen && shippingMode === 'manual')
    const canShip = order?.status === 'confirmed'
    const canDeliver = order?.status === 'shipped'


    return !order || !customer ? (<React.Fragment></React.Fragment>) : (
        <React.Fragment>
            <Card>
                <Row>
                    <Col sm={10}>
                        <nav className='Sales-orders-view-nav'>
                            <a className="btn btn-link p-0 m-0 d-inline align-baseline" href={`/sales-order/print/${order.id}`} rel="noopener noreferrer" target="_blank">
                                <i className='mdi mdi-printer'></i> Imprimir
                            </a>

                            <button
                                onClick={(e) => {
                                    e.preventDefault()
                                    _cancelOrder(order.id)
                                }}
                                className="btn btn-link p-0 m-0 d-inline align-baseline">
                                <i className='mdi mdi-cancel'></i> Cancelar Pedido
                            </button>

                            {profile.role.codeName === 'ADMIN' && (
                                <>
                                    { canConfirm && (
                                        <>
                                            <button
                                                onClick={(e) => {
                                                    e.preventDefault()
                                                    setConfirmOpen(true)
                                                }}
                                                className="btn btn-link p-0 m-0 d-inline align-baseline">
                                                <i className='mdi mdi-check'></i> Confirmar Pedido
                                            </button>
                                        </>
                                    )}

                                    { canShip && (
                                        <>
                                            <button
                                                onClick={(e) => {
                                                    e.preventDefault()
                                                    _markAsShipped(order.id)
                                                }}
                                                className="btn btn-link p-0 m-0 d-inline align-baseline">
                                                <i className='mdi mdi-check'></i> Marcar como Enviada
                                            </button>
                                        </>
                                    )}

                                    { canDeliver && (
                                        <>
                                            <button
                                                onClick={(e) => {
                                                    e.preventDefault()
                                                    _markAsDelivered(order.id)
                                                }}
                                                className="btn btn-link p-0 m-0 d-inline align-baseline">
                                                <i className='mdi mdi-check'></i> Marcar como Entregada
                                            </button>
                                        </>
                                    )}
                                </>
                            )}
                        </nav>
                    </Col>

                    <Col sm={2}><strong>#P-{order.folio}</strong> ({order.status})</Col>
                </Row>
            </Card>

            <Card>
                <section style={{ padding: 16 }}>
                    <h5>Flujo de envío</h5>
                    {canManageCost && <div style={{ marginBottom: 16 }}>
                        {manualShipment && <>
                            <Label for="actualShippingCost">Costo real de envío (MXN)</Label>
                            <Input id="actualShippingCost" type="number" min="0" step="0.01" value={actualCost} disabled={costBusy}
                                onChange={event => setActualCost(event.target.value)} />
                            <p>Opcional. Vacío significa pendiente; 0 confirma un costo de MXN $0.00.</p>
                            <Button disabled={costBusy} onClick={() => saveCost()}>Guardar costo de envío</Button>
                        </>}
                        {order.shipmentProvider === 'skydropx' && order.shipmentProviderShipmentId && actualCarrierCost(order) === null &&
                            <Button disabled={costBusy} onClick={() => saveCost(true)}>Recuperar costo de SkyDropX</Button>}
                        {costError && <p role="alert">{costError}</p>}
                    </div>}
                    {confirmOpen && canConfirm && <div style={{ marginBottom: 16 }}>
                        <Label for="confirmationShippingMode">Confirmar pedido</Label>
                        <Input id="confirmationShippingMode" type="select" disabled={confirming} value={shippingMode} onChange={event => setShippingMode(event.target.value)}>
                            <option value="skydropx">Generar guía con SkyDropX</option>
                            <option value="manual">Gestionar envío manualmente</option>
                        </Input>
                        {shippingMode === 'manual' && <p>Completa los datos de envío disponibles abajo. El rastreo es opcional y puede agregarse después.</p>}
                        {confirmationError && <p role="alert">{confirmationError}</p>}
                        <Button disabled={confirming} color="primary" onClick={() => _confirmOrder(order.id)}>{confirming ? 'Confirmando…' : 'Confirmar pedido'}</Button>{' '}
                        <Button disabled={confirming} onClick={() => setConfirmOpen(false)}>Cancelar</Button>
                    </div>}
                    <Row>
                        <Col sm={4}>
                            <strong>Cotización</strong><br />
                            {shippingQuote.provider ? (
                                <span>
                                    {shippingQuote.carrierDisplayName || shippingQuote.carrier || shippingQuote.provider}
                                    {shippingQuote.service ? ` / ${shippingQuote.service}` : ''}
                                    <br />
                                    {shippingQuote.amount ? (
                                        <CurrencyFormat decimalScale={2} displayType="text" thousandSeparator prefix="$" value={shippingQuote.amount} />
                                    ) : '-'} {shippingQuote.currency || ''}
                                </span>
                            ) : (
                                <span>Sin cotización registrada</span>
                            )}
                        </Col>
                        <Col sm={4}>
                            <strong>Fechas</strong><br />
                            Confirmado: {order.confirmedAt ? order.confirmedAt.substring(0, 10) : '-'}<br />
                            Enviado: {order.shippedAt ? order.shippedAt.substring(0, 10) : '-'}<br />
                            Entregado: {order.deliveredAt ? order.deliveredAt.substring(0, 10) : '-'}
                        </Col>
                        <Col sm={4}>
                            <strong>Estado</strong><br />
                            {order.status}
                        </Col>
                    </Row>

                    <hr />

                    <Row>
                        <Col sm={4}>
                            <Label>Paquetería</Label>
                            <Input
                                disabled={!canEditTracking}
                                value={trackingForm.carrier}
                                placeholder="DHL, FedEx, Estafeta..."
                                onChange={(event) => _updateTrackingField('carrier', event.target.value)}
                            />
                        </Col>
                        <Col sm={4}>
                            <Label>Número de rastreo</Label>
                            <Input
                                disabled={!canEditTracking}
                                value={trackingForm.trackingNumber}
                                onChange={(event) => _updateTrackingField('trackingNumber', event.target.value)}
                            />
                        </Col>
                        <Col sm={4}>
                            <Label>URL de rastreo</Label>
                            <Input
                                disabled={!canEditTracking}
                                value={trackingForm.trackingUrl}
                                onChange={(event) => _updateTrackingField('trackingUrl', event.target.value)}
                            />
                        </Col>
                    </Row>

                    <Row style={{ marginTop: 12 }}>
                        <Col sm={4}>
                            <Label>Proveedor</Label>
                            <Input
                                disabled={!canEditTracking}
                                value={trackingForm.shipmentProvider}
                                placeholder="manual / skydropx"
                                onChange={(event) => _updateTrackingField('shipmentProvider', event.target.value)}
                            />
                        </Col>
                        <Col sm={4}>
                            <Label>ID de envío proveedor</Label>
                            <Input
                                disabled={!canEditTracking}
                                value={trackingForm.shipmentProviderShipmentId}
                                onChange={(event) => _updateTrackingField('shipmentProviderShipmentId', event.target.value)}
                            />
                        </Col>
                        <Col sm={4}>
                            <Label>URL de etiqueta</Label>
                            <Input
                                disabled={!canEditTracking}
                                value={trackingForm.shipmentLabelUrl}
                                onChange={(event) => _updateTrackingField('shipmentLabelUrl', event.target.value)}
                            />
                        </Col>
                    </Row>

                    {['confirmed', 'shipped'].includes(order.status) && (
                        <Button
                            style={{ marginTop: 16 }}
                            color="primary"
                            onClick={() => _saveTracking(order.id)}>
                            Guardar rastreo
                        </Button>
                    )}
                </section>
            </Card>

            <Card>
                <section className="items">
                    <Table responsive>
                        <thead>
                            <tr>
                                <th>Imagen</th>
                                <th>Producto</th>
                                <th>CANT</th>
                                <th>Cliente</th>
                                <th>Precio unitario antes de descuentos</th>
                                <th>Importe antes de descuentos</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lineItems.map((item, i) => (
                                <tr key={i}>
                                    <td>
                                        <img src={item.productImage} width={64} alt="" />
                                    </td>
                                    <td>
                                        <strong>{item.productName}</strong> <em>{item.variant?.sku}</em><br />
                                        {item.categoryType === 'ARTIST' && (
                                            <span className="badge badge-warning">
                                                Artista invitado: {item.artistName} / {item.collectionName}
                                            </span>
                                        )}
                                        {<PersonalizationDetails item={item} />}
                                    </td>
                                    <td width={100}>
                                        <span>{item.quantity}</span>
                                    </td>
                                    <td width={150}>
                                        <span>{customer.fullName}</span>
                                    </td>
                                    <td width={150}>
                                        <CurrencyFormat decimalScale={2} displayType="text" thousandSeparator prefix="$" value={item.unitPrice} />
                                    </td>
                                    <td>
                                        <CurrencyFormat decimalScale={2} displayType="text" thousandSeparator prefix="$" value={(item.unitPrice * item.quantity)} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <FinancialBreakdown order={order} />
                </section>
            </Card>
        </React.Fragment>
    )
}

const mapStatetoProps = (state) => {
    return {
        profile: state.Auth.profile
    }
}

export default connect(
    mapStatetoProps, {}
)(withRouter(View))
